import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  inject,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { logoUrl } from '../../utility/logo';
import { ClearZeroOnFocusDirective } from '../../utility/clear-zero-on-focus.directive';
import { InfoTooltipComponent } from '../info-tooltip/info-tooltip.component';
import { PhoneInputComponent } from '../../utility/phone-input/phone-input.component';
import { SignaturePadComponent } from '../../utility/signature-pad/signature-pad.component';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { ConnectionService } from '../../service/connection.service';
import { SwalService } from '../../service/swal.service';
import { SubscriptionService } from '../../service/subscription.service';
import { EventService } from '../../service/event.service';
import { environment } from '../../../environments/environment';
import { resolveDepositEuro } from '../../utility/deposit';
import { IconComponent } from '../icon/icon.component';
import { LoadErrorComponent } from '../load-error/load-error.component';

@Component({
  selector: 'app-contract-settings',
  imports: [CommonModule, FormsModule, ReactiveFormsModule, TranslatePipe, ClearZeroOnFocusDirective, InfoTooltipComponent, PhoneInputComponent, SignaturePadComponent, IconComponent, LoadErrorComponent],
  templateUrl: './contract-settings.component.html',
  styleUrl: './contract-settings.component.css',
})
export class ContractSettingsComponent implements OnInit, OnChanges {
  private fb = inject(FormBuilder);
  private connection = inject(ConnectionService);
  private swal = inject(SwalService);
  private subscription = inject(SubscriptionService);
  private cdr = inject(ChangeDetectorRef);
  private sanitizer = inject(DomSanitizer);
  private router = inject(Router);
  private eventService = inject(EventService);
  private translate = inject(TranslateService);

  /** Se valorizzato, il componente lavora in contesto evento: salva un override per-evento invece dei dati profilo. */
  @Input() eventId?: string;
  @Input() eventContractOverride: Record<string, any> | null = null;
  @Input() readOnly = false;
  /** Totale dell'evento (contesto evento): serve a risolvere l'acconto percentuale in euro. */
  @Input() eventTotal = 0;
  /** Acconto reale dell'evento (financial.deposit): in contesto evento è l'unica fonte di verità per importo/unità. */
  @Input() eventDeposit: { cost?: number; type?: 'fixed' | 'percent'; paid?: boolean; note?: string } | null = null;
  @Output() overrideSaved = new EventEmitter<Record<string, any> | null>();
  @Output() depositSaved = new EventEmitter<{ cost: number; type: 'fixed' | 'percent' }>();

  user: any;
  contractForm!: FormGroup;
  signaturePreview: string | null = null;
  logoPreview: string | null = null;
  /** Mostra il totale nel PDF contratto (impostazione di profilo, non per-evento). */
  show_total = true;
  /** Wizard aperto come tappa 4 di 5 del primo accesso (vedi onboarding). */
  readonly onboardingChain = localStorage.getItem('post_onboarding_chain') === 'true';

  modifyContract = false;
  isWizardMode = false;
  wizardIntro = false;
  contractStep = 0; // 0=dati studio, 1=pagamento, 2=location, 3=cancellazione, 4=template+firma
  sectionEditState: { [key: string]: boolean } = {};

  get overrideActive(): boolean {
    return (
      !!this.eventId &&
      !!this.eventContractOverride &&
      Object.keys(this.eventContractOverride).length > 0
    );
  }

  @ViewChild(SignaturePadComponent) sigPad?: SignaturePadComponent;

  templates = [
    { id: 'default', label: 'Default', desc: 'Template originale' },
    { id: 'ivory', label: 'Ivory Classic', desc: 'Avorio caldo, serif, accenti oro' },
    { id: 'blanc', label: 'Blanc Minimal', desc: 'Bianco, geometria pura' },
    { id: 'blush', label: 'Blush Romance', desc: 'Titoli rosa antico, serif italico' },
    { id: 'noir', label: 'Noir Luxury', desc: 'Sfondo scuro, testo oro' },
  ];

  get canProThemes(): boolean {
    return this.subscription.canPro();
  }

  /** Seleziona un tema: quelli diversi da 'default' richiedono il piano Pro. */
  selectTemplate(id: string): void {
    if (
      id !== 'default' &&
      !this.subscription.requirePro(
        'I temi del contratto sono disponibili con il piano Pro o Agency.',
      )
    ) {
      return;
    }
    this.contractForm.patchValue({ template: id });
  }

  // Contratto non arrivato: il modulo resta nascosto, salvarlo vuoto cancellerebbe quello vero
  loadError = false;

  ngOnInit() {
    this.subscription.loadEntitlements();
    this.load();
  }

  load() {
    this.connection.get(
      'auth/profile',
      (data: any) => {
        this.loadError = false;
        this.user = data;
        this.show_total = data.show_total ?? true;
        const profileContract = data.contract ?? {};
        const c = this.overrideActive
          ? this.eventContractOverride!
          : profileContract;
        this.initContractForm(c, data.user ?? {});
        if (profileContract.signature && !profileContract.signature.startsWith('data:')) {
          this.signaturePreview = `${environment.apiUrl}/auth/signature/${data._id}?t=${Date.now()}`;
        }
        if (profileContract.logo) {
          this.logoPreview = logoUrl(data._id, true);
        }
        if (!this.eventId && localStorage.getItem('contract_wizard_pending') === 'true') {
          localStorage.removeItem('contract_wizard_pending');
          this.openWizardWithIntro();
        }
      },
      () => (this.loadError = true),
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    const overrideChanged = changes['eventContractOverride'] && !changes['eventContractOverride'].firstChange;
    const depositChanged = changes['eventDeposit'] && !changes['eventDeposit'].firstChange;
    if ((overrideChanged || depositChanged) && this.user) {
      const c = this.overrideActive
        ? this.eventContractOverride!
        : (this.user.contract ?? {});
      this.initContractForm(c, this.user.user ?? {});
    }
  }

  private initContractForm(c: any, u: any = {}) {
    // Prima volta (nessun dato contratto): precompila dai dati del profilo
    const defaultBusinessName = c.business_name || u.organization || '';
    const defaultFullName =
      c.full_name || `${u.name ?? ''} ${u.lastname ?? ''}`.trim();

    // In contesto evento, importo e unità dell'acconto vengono dal tracking
    // pagamenti reale (financial.deposit), non dal template contratto —
    // è l'unica fonte di verità condivisa col tab Pagamenti.
    const depositAmount = this.eventId
      ? (this.eventDeposit?.cost ?? 0)
      : (c.payment?.deposit_amount ?? 0);
    const depositType = this.eventId
      ? (this.eventDeposit?.type ?? 'fixed')
      : (c.payment?.deposit_type ?? 'fixed');

    this.contractForm = this.fb.group({
      // Dati studio
      business_name: [defaultBusinessName],
      full_name: [defaultFullName],
      address: [c.address ?? ''],
      telephone: [c.telephone ?? ''],
      email: [c.email ?? '', Validators.email],
      piva: [c.piva ?? ''],
      travel_rate_per_km: [c.travel_rate_per_km ?? 0],

      // Pagamento
      deposit_amount: [depositAmount],
      deposit_type: [depositType],
      deposit_method: [c.payment?.deposit_method ?? 'cash'],
      final_payment_method: [c.payment?.final_payment_method ?? 'cash'],
      final_payment_when: [c.payment?.final_payment_when ?? 'wedding_day'],
      final_payment_days: [c.payment?.final_payment_days ?? null],
      iban: [c.payment?.iban ?? ''],

      // Location
      needs_table: [c.location?.needs_table ?? true],
      needs_natural_light: [c.location?.needs_natural_light ?? true],
      needs_power_outlet: [c.location?.needs_power_outlet ?? true],
      location_notes: [c.location?.extra_notes ?? ''],

      // Cancellazione
      deposit_refundable: [c.cancellation?.deposit_refundable ?? false],
      postponement_policy: [c.cancellation?.postponement_policy ?? 'transfer_date'],
      cancellation_notes: [c.cancellation?.extra_notes ?? ''],

      // Template
      template: [c.template ?? 'default'],

      // Altro (sezione libera: titolo + testo)
      altro_title: [c.altro?.title ?? ''],
      altro_text: [c.altro?.text ?? ''],
    });
  }

  // Controlla se il contratto è compilato (per wizard vs modifica)
  get contractIsEmpty(): boolean {
    const c = this.user?.contract;
    return !c?.business_name && !c?.full_name;
  }

  // Apri wizard
  openWizard() {
    this.wizardIntro = false;
    this.isWizardMode = true;
    this.contractStep = 0;
    this.modifyContract = true;
  }

  openWizardWithIntro() {
    this.openWizard();
    this.wizardIntro = true;
  }

  // Apri modifica libera
  openFreeEdit() {
    this.isWizardMode = false;
    this.modifyContract = true;
  }

  // Apri sezione specifica
  openSection(section: string) {
    this.sectionEditState[section] = true;
  }

  closeSection(section: string) {
    this.sectionEditState[section] = false;
  }

  // Salva sezione specifica
  saveSection(section: string) {
    this.saveContract();
    this.sectionEditState[section] = false;
  }

  saveShowTotal(): void {
    this.connection.put(
      'auth/show-total',
      { show_total: this.show_total },
      () => {},
      () => this.swal.errorDialog('Errore', 'Salvataggio fallito'),
    );
  }

  // Wizard: avanti/indietro
  nextStep() {
    if (!this.isStepValid()) {
      const v = this.contractForm.value;
      const missing: string[] = [];
      if (this.contractStep === 0) {
        if (!v.business_name?.trim()) missing.push('Nome studio/attività');
        if (!v.full_name?.trim()) missing.push('Nome completo');
      }
      this.swal.listWarning(
        'Campi obbligatori',
        missing,
        'Compila questi campi per continuare:',
      );
      return;
    }
    if (this.contractStep < 4) this.contractStep++;
  }

  prevStep() {
    if (this.contractStep > 0) this.contractStep--;
  }

  // Validazione per step
  isStepValid(): boolean {
    switch (this.contractStep) {
      case 0:
        return (
          !!this.contractForm.value.business_name &&
          !!this.contractForm.value.full_name
        );
      case 1:
        return this.contractForm.value.deposit_amount >= 0;
      default:
        return true;
    }
  }

  // Preview testo clausola pagamento
  /** Controvalore in euro dell'acconto (risolve la percentuale sul totale evento, se in contesto evento). */
  get resolvedDepositEuro(): number {
    const v = this.contractForm?.value;
    if (!v) return 0;
    return resolveDepositEuro({ cost: v.deposit_amount, type: v.deposit_type }, this.eventTotal);
  }

  get paymentPreview(): string {
    const v = this.contractForm?.value;
    if (!v) return '';
    const method = {
      cash: 'in contanti',
      transfer: 'tramite bonifico',
      both: 'in contanti o bonifico',
    };
    const when =
      v.final_payment_when === 'wedding_day'
        ? 'il giorno del matrimonio'
        : `${v.final_payment_days} giorni prima del matrimonio`;
    let depositText: string;
    if (v.deposit_type === 'percent') {
      depositText = `pari al ${v.deposit_amount}% del totale`;
      if (this.eventId && this.eventTotal > 0) {
        depositText += ` (€${this.resolvedDepositEuro.toFixed(2)})`;
      }
    } else {
      depositText = `di €${v.deposit_amount}`;
    }
    return `Il cliente versa un acconto ${depositText} alla firma del contratto, pagabile ${method[v.deposit_method as keyof typeof method]}. Il saldo verrà corrisposto ${when}, ${method[v.final_payment_method as keyof typeof method]}.`;
  }

  get locationPreview(): string {
    const v = this.contractForm?.value;
    if (!v) return '';
    const reqs = [];
    if (v.needs_table) reqs.push('un tavolo da lavoro');
    if (v.needs_natural_light) reqs.push('luce naturale adeguata');
    if (v.needs_power_outlet) reqs.push('una presa elettrica');
    const base = reqs.length
      ? `È necessario disporre di: ${reqs.join(', ')}.`
      : '';
    return v.location_notes ? `${base} ${v.location_notes}` : base;
  }

  get cancellationPreview(): string {
    const v = this.contractForm?.value;
    if (!v) return '';
    const refund = v.deposit_refundable ? 'rimborsabile' : 'non rimborsabile';
    const postpone =
      v.postponement_policy === 'transfer_date'
        ? "In caso di rinvio, l'acconto verrà trasferito alla nuova data."
        : "In caso di rinvio, l'acconto non verrà rimborsato.";
    return `In caso di cancellazione, l'acconto è ${refund}. ${postpone}${v.cancellation_notes ? ' ' + v.cancellation_notes : ''}`;
  }

  // Salva contratto con nuovo schema
  saveContract() {
    const v = this.contractForm.value;

    // Campi obbligatori: nome studio/attività e nome completo
    if (!v.business_name?.trim() || !v.full_name?.trim()) {
      this.swal.errorDialog(
        'Campi obbligatori',
        'Compila il nome dello studio/attività e il nome completo prima di salvare.',
      );
      return;
    }

    const payload = {
      business_name: v.business_name,
      full_name: v.full_name,
      address: v.address,
      telephone: v.telephone,
      email: v.email,
      piva: v.piva,
      travel_rate_per_km: v.travel_rate_per_km,
      payment: {
        // In contesto evento importo/unità dell'acconto vivono in
        // financial.deposit (vedi sotto), non nell'override contratto.
        ...(this.eventId ? {} : { deposit_amount: v.deposit_amount, deposit_type: v.deposit_type }),
        deposit_method: v.deposit_method,
        final_payment_method: v.final_payment_method,
        final_payment_when: v.final_payment_when,
        final_payment_days: v.final_payment_days,
        iban: v.iban,
      },
      location: {
        needs_table: v.needs_table,
        needs_natural_light: v.needs_natural_light,
        needs_power_outlet: v.needs_power_outlet,
        extra_notes: v.location_notes,
      },
      cancellation: {
        deposit_refundable: v.deposit_refundable,
        postponement_policy: v.postponement_policy,
        extra_notes: v.cancellation_notes,
      },
      template: v.template,
      altro: {
        title: v.altro_title,
        text: v.altro_text,
      },
    };

    if (this.eventId) {
      this.eventService.saveContractOverride(
        this.eventId,
        payload,
        () => {
          this.eventContractOverride = payload;
          this.overrideSaved.emit(payload);
          this.swal.success('Dati contratto evento salvati!');
          this.cdr.detectChanges();
        },
        () => this.swal.errorDialog('Errore', 'Salvataggio dati contratto fallito'),
      );
      // L'acconto (importo/unità) è condiviso col tab Pagamenti: si scrive
      // sempre su financial.deposit, preservando paid/note già impostati.
      this.eventService.updateFinancial(
        this.eventId,
        {
          deposit: {
            cost: v.deposit_amount,
            type: v.deposit_type,
            paid: this.eventDeposit?.paid ?? false,
            note: this.eventDeposit?.note ?? '',
          },
        },
        () => this.depositSaved.emit({ cost: v.deposit_amount, type: v.deposit_type }),
        () => {},
      );
      return;
    }

    const wasWizard = this.isWizardMode;

    this.connection.put(
      'auth/contract',
      payload,
      () => {
        this.modifyContract = false;
        this.isWizardMode = false;
        this.swal.success('Dati contratto salvati!');
        if (!this.user.contract) this.user.contract = {};
        Object.assign(this.user.contract, payload);
        this.cdr.detectChanges();

        if (wasWizard && localStorage.getItem('post_onboarding_chain') === 'true') {
          localStorage.removeItem('post_onboarding_chain');
          localStorage.setItem('listino_intro_pending', 'true');
          this.router.navigate(['/listino']);
        }
      },
      () => this.swal.errorDialog('Errore', 'Salvataggio dati contratto fallito'),
    );
  }

  /** Azzera l'override per-evento e ripristina i valori del profilo nel form. */
  resetOverride(): void {
    if (!this.eventId) return;
    this.swal
      .confirm('Ripristina dal profilo', 'Ricaricare i dati contratto dal profilo?')
      .then((r) => {
        if (!r.isConfirmed) return;
        this.eventService.saveContractOverride(
          this.eventId!,
          null,
          () => {
            this.eventContractOverride = null;
            this.overrideSaved.emit(null);
            this.initContractForm(this.user?.contract ?? {}, this.user?.user ?? {});
            this.swal.success('Ripristinato dal profilo');
            this.cdr.detectChanges();
          },
          () => this.swal.errorDialog('Errore', 'Ripristino fallito'),
        );
      });
  }

  // ── ANTEPRIMA TEMPLATE ────────────────────────────────────────────────────
  previewOpen = false;
  previewUrl: SafeResourceUrl | null = null;
  private previewObjectUrl: string | null = null;

  anteprimaTemplate() {
    const v = this.contractForm.value;
    const contract_info: any = {
      business_name: v.business_name || 'Nome Studio',
      full_name: v.full_name || 'Nome Cognome',
      address: v.address ?? '',
      telephone: v.telephone ?? '',
      email: v.email ?? '',
      piva: v.piva ?? '',
      payment: {
        deposit_amount: v.deposit_amount,
        deposit_type: v.deposit_type,
        deposit_method: v.deposit_method,
        final_payment_method: v.final_payment_method,
        final_payment_when: v.final_payment_when,
        final_payment_days: v.final_payment_days,
        iban: v.iban,
      },
      location: {
        needs_table: v.needs_table,
        needs_natural_light: v.needs_natural_light,
        needs_power_outlet: v.needs_power_outlet,
        extra_notes: v.location_notes,
      },
      cancellation: {
        deposit_refundable: v.deposit_refundable,
        postponement_policy: v.postponement_policy,
        extra_notes: v.cancellation_notes,
      },
      template: v.template || 'default',
      lang: 'it',
      signature: null,
      sections: null,
      altro: { title: v.altro_title, text: v.altro_text },
      bride_signature: null,
    };

    this.connection.postBlob(
      'events/contract-pdf/sample',
      { info: contract_info, lang: 'it' },
      (blob: Blob) => {
        this.clearPreviewUrl();
        this.previewObjectUrl = URL.createObjectURL(blob);
        this.previewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
          this.previewObjectUrl,
        );
        this.previewOpen = true;
      },
      () => this.swal.error('Errore nella generazione dell\'anteprima'),
    );
  }

  closePreview() {
    this.previewOpen = false;
    this.clearPreviewUrl();
  }

  private clearPreviewUrl() {
    if (this.previewObjectUrl) {
      URL.revokeObjectURL(this.previewObjectUrl);
      this.previewObjectUrl = null;
    }
    this.previewUrl = null;
  }

  // ── LOGO ──────────────────────────────────────────────────────────────────
  onLogoSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      this.swal.error(this.translate.instant('profile.logo.invalid'));
      return;
    }
    const formData = new FormData();
    formData.append('logo', file);
    this.connection.post(
      'auth/upload-logo',
      formData,
      (data: any) => {
        if (!this.user.contract) this.user.contract = {};
        this.user.contract.logo = data.key;
        this.logoPreview = logoUrl(this.user._id, true);
        this.swal.success(this.translate.instant('profile.logo.saved'));
        this.cdr.detectChanges();
      },
      () => this.swal.errorDialog(this.translate.instant('swal.error'), this.translate.instant('profile.logo.upload_error')),
    );
  }

  removeLogo() {
    this.connection.delete(
      'auth/logo',
      () => {
        if (this.user.contract) this.user.contract.logo = null;
        this.logoPreview = null;
        this.swal.success(this.translate.instant('profile.logo.removed'));
        this.cdr.detectChanges();
      },
      () => this.swal.errorDialog(this.translate.instant('swal.error'), this.translate.instant('profile.logo.remove_error')),
    );
  }

  // ── FIRMA ─────────────────────────────────────────────────────────────────
  clearSignature() {
    this.sigPad?.clear();
  }

  get canvasEmpty(): boolean {
    return this.sigPad?.isEmpty ?? true;
  }

  saveSignature() {
    const blob = this.sigPad?.getBlob();
    if (!blob) {
      this.swal.error('Disegna o carica prima la tua firma');
      return;
    }
    const formData = new FormData();
    formData.append('signature', blob, 'signature.png');
    this.connection.post(
      'auth/upload-signature',
      formData,
      (data: any) => {
        if (!this.user.contract) this.user.contract = {};
        this.user.contract.signature = data.key;
        this.signaturePreview = `${environment.apiUrl}/auth/signature/${this.user._id}?t=${Date.now()}`;
        this.swal.success('Firma salvata!');
        this.cdr.detectChanges();
      },
      () => this.swal.errorDialog('Errore', 'Upload firma fallito'),
    );
  }
}
